import apiService from './api/ApiService';
import sessionReader from '../store/readHelpers/SessionReader';
import storeManager from '../store/StoreManager';
import stateCacheService from './StateCache';
import timeActions from '../actions/TimeActions';
import utilService from './UtilService';
import environmentNames from '../qti/environmentNames';
import parentTestService from './ParentTestService';
import { reportError } from './ErrorReporting';
import marketplacePropertiesService from './MarketplacePropertiesService';
import candidateWarningConstants from '../constants/CandidateWarningConstants';
import remainingTimeHandler from './remainingTime/RemainingTimeHandler';
import remainingTimeConstants from './remainingTime/RemainingTimeConstants';
import reloginService from './ReloginService';

// need this variable protected (out of the timeService object) to avoid binding and be able to remove window.offline event listener
let isStalePeriod = false;

const timeService = {
    assessmentRunId: null,
    reportTimeoutWarningToMonitorCalled: false,
    handleForFetchTimeRemaining: undefined,
    handleForUpdateTimeRemaining: undefined,
    lastExposedToTestTimeTrackId: undefined,

    /**
     * @param {int} minutesToAdd
     */
    addTimeInMinutes(minutesToAdd) {
        const { time } = storeManager.getState();
        const timeNow = utilService.getCurrentDate().getTime();
        const msToAdd = minutesToAdd * remainingTimeConstants.MINUTE_MILLISECONDS;
        const timeRemaining = time.timeRemaining + msToAdd;

        storeManager.dispatch(
            timeActions.setTimeRemainingFetchedFromServer(
                timeRemaining,
                timeNow,
                timeRemaining,
                timeNow,
                time.testDurationUsed,
                time.testDuration,
                time.pauseTime,
                time.endTime,
            )
        );
    },

    async fetchTimeRemaining(withRetry = true, skipFrontCache = false) {
        if (process.env.NODE_ENV === environmentNames.DEVELOPMENT) {
            return false;
        }

        const shouldSkipFrontCache = skipFrontCache || this.shouldForceSkipFrontCache();
        if (shouldSkipFrontCache) {
            // Restart the timer for fetching time remaining to be sure not to load stale data
            this.scheduleFetchingOfTimeRemaining();
        }

        parentTestService.verifyParentChildContext();
        const assessmentRunIdToUse = this.assessmentRunId;
        return apiService.fetchRemainingTime(assessmentRunIdToUse, withRetry, shouldSkipFrontCache)
            .then((response) => {
                this.endStalePeriod();
                const currentAssessmentRunId = sessionReader.getAssessmentRunId();
                if (assessmentRunIdToUse !== currentAssessmentRunId) {
                    window.console.log(`*** Ignoring GetRemainingTime response from previous assessmentRunId ${assessmentRunIdToUse}.`);
                    return undefined;
                }
                return remainingTimeHandler.handleTimeRemainingResponse(this.assessmentRunId, response);
            })
            .catch(() => {
                return undefined;
            });
    },

    shouldForceSkipFrontCache() {
        return isStalePeriod && reloginService.shouldRefreshUserTestSetupUponBackOnline();
    },

    addOfflinePeriodMonitoring() {
        if (reloginService.shouldRefreshUserTestSetupUponBackOnline()) {
            window.addEventListener('offline', this.startStalePeriod);
        }
    },

    startStalePeriod() {
        isStalePeriod = true;
    },

    endStalePeriod() {
        isStalePeriod = false;
    },

    reportTimeoutWarningToMonitor() {
        const { time } = storeManager.getState();
        const timeRemaining = time.timeRemaining;
        const isOpenForReDelivery = sessionReader.isOpenForReDelivery();

        if (!marketplacePropertiesService.isCandidateTimeoutWarningEnabled() ||
            sessionReader.isParentTest() ||
            this.reportTimeoutWarningToMonitorCalled ||
            isOpenForReDelivery
        ) {
            return;
        }

        if (timeRemaining === 0) {
            apiService.notifyClientEvent(
                candidateWarningConstants.TIMED_OUT,
                '',
                true
            ).catch((error) => {
                window.console.log(error);
                this.reportTimeoutWarningToMonitorCalled = false;
            });
            this.reportTimeoutWarningToMonitorCalled = true;
        }
    },

    /**
     * @param {int} timeRemaining in ms
     * @param {boolean} durationUsed
     * @param {int} assessmentDuration
     * @param {int} pauseTime ???
     * @param {int} endTime
     */
    dispatchTimeRemaining(timeRemaining, durationUsed, assessmentDuration, pauseTime, endTime) {
        const timeNow = utilService.getCurrentDate().getTime();

        storeManager.dispatch(timeActions.setTimeRemainingFetchedFromServer(
            timeRemaining,
            timeNow,
            timeRemaining,
            timeNow,
            durationUsed,
            assessmentDuration,
            pauseTime,
            endTime,
        ));
    },

    updateTimeRemaining() {
        const {
            finalMinutesMode,
            timeRemaining,
            timeRemainingFetchedFromServer,
            timeRemainingFetchedFromServerAt,
            pauseTime,
        } = storeManager.getState().time;
        const now = utilService.getCurrentDate();

        if (timeRemainingFetchedFromServerAt === undefined) {
            // Time remaining has not been fetched yet.
            return;
        }

        if (pauseTime && marketplacePropertiesService.isPauseWithStopTimeEnabled()) {
            // Await the updated time remaining from server to restart the counting
            return;
        }

        const newTimeRemaining = this.calculateTimeRemaining(timeRemainingFetchedFromServer, timeRemainingFetchedFromServerAt, now);
        storeManager.dispatch(
            timeActions.setTimeRemaining(newTimeRemaining, now)
        );

        remainingTimeHandler.handleTimeRemainingChange(newTimeRemaining, timeRemaining, finalMinutesMode);
    },

    updateTimeRemainingFromCache() {
        const cachedTimeState = stateCacheService.getStateCacheInstance().getParentTime();
        if (!cachedTimeState) {
            return;
        }

        const {
            endTime,
            finalMinutesMode,
            pauseTime,
            testDurationUsed,
            testDuration,
            timeRemainingFetchedFromServer,
            timeRemainingFetchedFromServerAt,
        } = cachedTimeState;
        const now = utilService.getCurrentDate();

        const newTimeRemaining = this.calculateTimeRemaining(timeRemainingFetchedFromServer, timeRemainingFetchedFromServerAt, now);

        this.dispatchTimeRemaining(newTimeRemaining, testDurationUsed, testDuration, pauseTime, endTime);
        storeManager.dispatch(
            timeActions.setTimeRemaining(newTimeRemaining, now)
        );

        remainingTimeHandler.handleTimeRemainingChange(newTimeRemaining, -1, finalMinutesMode);
    },

    calculateTimeRemaining(timeRemainingFetchedFromServer, timeRemainingFetchedFromServerAt, now) {
        const timeSinceLastFetch = now - timeRemainingFetchedFromServerAt;
        const timeDiff = timeRemainingFetchedFromServer - timeSinceLastFetch;

        if (timeRemainingFetchedFromServer === -1) {
            // when test is open for redelivery for current student
            return timeRemainingFetchedFromServer;
        }

        return timeDiff > 0 ? timeDiff : 0;
    },

    stopTime() {
        this.cancelTimeRemainingUpdating();
    },

    resumeTime() {
        if (this.handleForUpdateTimeRemaining === undefined) {
            // If finalMinutesMode changes, the interval will be started. Otherwise, re-start the interval based on the most recent update from server
            const { time } = storeManager.getState();
            const interval = time.finalMinutesMode || marketplacePropertiesService.showRemainTimeSeconds()
                ? remainingTimeConstants.SHOW_SECONDS_TIME_REMAINING_UPDATE_INTERVAL
                : remainingTimeConstants.TIME_REMAINING_UPDATE_INTERVAL;
            this.updateTimeRemaining();
            this.scheduleUpdateOfTimeRemaining(interval);
        }
    },

    switchOnFinalMinutesMode() {
        storeManager.dispatch(timeActions.setLastMinutesMode(true));
        this.scheduleUpdateOfTimeRemaining(remainingTimeConstants.SHOW_SECONDS_TIME_REMAINING_UPDATE_INTERVAL);
    },

    switchOffFinalMinutesMode() {
        if (!marketplacePropertiesService.showRemainTimeSeconds()) {
            this.scheduleUpdateOfTimeRemaining(remainingTimeConstants.TIME_REMAINING_UPDATE_INTERVAL);
        }
        storeManager.dispatch(
            timeActions.setLastMinutesMode(false)
        );
    },

    scheduleUpdateOfTimeRemaining(interval = remainingTimeConstants.TIME_REMAINING_UPDATE_INTERVAL) {
        this.cancelTimeRemainingUpdating();
        this.handleForUpdateTimeRemaining = setInterval(
            this.updateTimeRemaining.bind(this)
            , interval
        );
    },

    scheduleFetchingOfTimeRemaining(interval = remainingTimeConstants.TIME_REMAINING_UPDATE_FROM_SERVER_INTERVAL) {
        this.cancelTimeRemainingFetching();
        this.handleForFetchTimeRemaining = setInterval(this.fetchTimeRemaining.bind(this), interval);
        this.addOfflinePeriodMonitoring();
    },

    async start(assessmentRunId = sessionReader.getAssessmentRunId()) {
        this.setAssessmentRunId(assessmentRunId);

        if (!sessionReader.isOfflineMode()) {
            try {
                await this.fetchTimeRemaining();
            } catch (error) {
                reportError(error);
            }

            this.scheduleFetchingOfTimeRemaining(remainingTimeConstants.TIME_REMAINING_UPDATE_FROM_SERVER_INTERVAL);
        }

        this.updateTimeRemaining();

        if (!this.handleForUpdateTimeRemaining) {
            // if not updateTimeRemaining has sent the user into finalMinutesMode start with updates based on the MPP
            const interval = marketplacePropertiesService.showRemainTimeSeconds()
                ? remainingTimeConstants.SHOW_SECONDS_TIME_REMAINING_UPDATE_INTERVAL
                : remainingTimeConstants.TIME_REMAINING_UPDATE_INTERVAL;
            this.scheduleUpdateOfTimeRemaining(interval);
        }

        if (sessionReader.shouldStopTimeUponExit() && !sessionReader.isParentTestContextOutsideChildTest()) {
            this.startTrackingLastExposedTime(assessmentRunId);
        }
    },

    setAssessmentRunId(assessmentRunId) {
        this.assessmentRunId = assessmentRunId;
    },

    cancelSchedules() {
        this.cancelTimeRemainingFetching();
        this.cancelTimeRemainingUpdating();
    },

    cancelTimeRemainingFetching() {
        clearInterval(this.handleForFetchTimeRemaining);
        this.handleForFetchTimeRemaining = undefined;
        window.removeEventListener('offline', this.startStalePeriod);
    },

    cancelTimeRemainingUpdating() {
        clearInterval(this.handleForUpdateTimeRemaining);
        this.handleForUpdateTimeRemaining = undefined;
    },

    startTrackingLastExposedTime(assessmentRunId) {
        this.lastExposedToTestTimeTrackId = setInterval(() => {
            stateCacheService.getStateCacheInstance().updateLastExposedTime(assessmentRunId);
        }, 1000);
    },

    stopLastExposedTimeTracking() {
        clearInterval(this.lastExposedToTestTimeTrackId);
        this.lastExposedToTestTimeTrackId = undefined;
    },
};

export default timeService;
